import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { darken, IconButton, Stack, Typography } from '@mui/material';
import { LinkedinTypes, ProductGroups } from 'enums';
import {
  FileUpload,
  Funnel,
  FunnelContact,
  FunnelStatuses,
  FunnelTypes,
  NodeVariants,
  RecruiterLocationSearch,
  RegularConstraint,
  Search,
} from 'features/co-pilot/co-pilot.types';
import { Blacklist_New } from 'features/co-pilot/company-blacklists/company-blacklist.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node } from 'reactflow';
import useCampaignNodeStore from 'stores/campaign-node-store';
import { makeStyles } from 'tss-react/mui';
import {
  AutoReplyCase,
  BlacklistScopes,
  CampaignBuilderSteps,
  CampaignPipelineSteps,
  CampaignTestingState,
  ContactFilter,
  ContactSearchMethods,
  ContactToAdd,
  CustomPlaceholder,
  LinkedinSearch,
  LinkedinSearchResult,
  Offer,
  QualificationPromptPlaceholders,
  Qualifications,
  SearchObjective,
  SearchToAdd,
  SelectorIds,
  SingleSearch,
} from '../campaign-builder.types';
import BriefAi from './brief-ai';
import CampaignFlowManager from './campaign-flow-manager/campaign-flow-manager';
import CampaignPipeline from './campaign-pipeline/campaign-pipeline';
import CampaignSettings from './campaign-settings/campaign-settings';
import CampaignSummary from './campaign-summary/campaign-summary';
import CampaignTesting from './campaign-testing/campaign-testing';
import StepsSidebar from './steps-sidebar';

interface Props {
  productGroup: ProductGroups;
  campaign: Funnel | null;
  hasJudgingSettings: boolean;

  fullLinkedinAccount: LinkedinAccount | undefined;
  linkedinAccountLoading: boolean;

  fullSeparateLinkedinAccount: LinkedinAccount | undefined;

  autoSyncLoading: boolean;

  isUpdating: boolean;
  isFlowChanging: boolean;

  expanded: boolean;
  activeCampaignStepIndex: number;

  perplexityUrl: string;
  perplexityLoading: boolean;
  perplexityError: {
    noJobAd: boolean;
    noJobTitle: boolean;
    noProblems: boolean;
    noFeatures: boolean;
    noBenefits: boolean;
    noImpact: boolean;
  };
  perplexityStatusMessage: string;
  offer: Offer;

  activeCampaignPipelineStep: CampaignPipelineSteps;
  contactSearchMethod: ContactSearchMethods | '';

  linkedinProduct: LinkedinTypes | '';
  paiSearchLanguage: string[];
  includeOwnContacts: boolean;
  regularLocationConstraints: RegularConstraint;
  recruiterLocationConstraints: RecruiterLocationSearch;
  linkedinSearch: LinkedinSearch;

  selectedContactSearchObjective: SearchObjective | null;
  searchToAdd: SearchToAdd | null;
  separateLinkedinAccount: string;
  addSearchesLoading: boolean;

  contactsToAdd: ContactToAdd[];
  addContactsLoading: boolean;

  searches: Search[];
  searchesLoading: boolean;

  linkedinAccounts: LinkedinAccount[];
  activeBlacklistScope: BlacklistScopes;
  globalBlacklists: Blacklist_New[];
  localBlacklists: Blacklist_New[];
  globalBlacklistsLoading: boolean;
  localBlacklistsLoading: boolean;
  removeBlacklistItemsLoading: boolean;

  contactsPage: number;
  contactsSearchParam: string;
  contactsPerPage: number;
  totalContacts: number;
  contactFilter: ContactFilter;
  activeQualifications: Qualifications[];
  abortRequested: boolean;
  abortLoading: boolean;
  deleteMultipleContacts: boolean;
  contactsToDelete: FunnelContact[];

  aiJudgementProcessing: boolean;
  profileAnalysisProcessing: boolean;
  contacts: FunnelContact[];
  contactsLoading: boolean;

  campaignTesting: CampaignTestingState;

  finalContacts: FunnelContact[];
  finalContactsPage: number;
  finalContactsSearchParam: string;
  finalContactsPerPage: number;
  totalFinalContacts: number;
  finalContactsLoading: boolean;
  finalContactsProcessing: boolean;
  exportFinalContactsLoading: boolean;

  selectCampaignStep(idx: number): () => void;

  handlePerplexityUrlChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleOfferInputChange(type: keyof Offer): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleOfferToggleChange(type: keyof Offer): (inUse: boolean) => void;

  handlePerplexityPaste(event: React.ClipboardEvent): void;

  handlePerplexityKeyDown(event: React.KeyboardEvent): void;

  handlePerplexityRefresh(): void;

  handleActiveCampaignPipelineStepChange(newStep: CampaignPipelineSteps): () => void;

  handleContactSearchMethodChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleMoveToNewSearch(): void;

  handleLinkedinProductChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handlePaiSearchLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIncludeOwnContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLocationFieldChange(event: React.ChangeEvent<HTMLInputElement>): void;

  searchKeyUp(): void;

  searchKeyDown(): void;

  handleMultiSelectLocationsChange(value: string): () => void;

  handleMultiSelectLocationsRemove(value: string): () => void;

  handleSelectRecruiterLocationConstraint(searchType: 'geo' | 'zip'): (value: string) => () => void;

  handleRemoveRecruiterLocationConstraint(searchType: 'geo' | 'zip', urn: string): () => void;

  handleRecruiterLocationConstraintQueryChange(searchType: 'geo' | 'zip'): (event: React.ChangeEvent<HTMLInputElement>) => void;

  searchRecruiterLocationKeyUp(searchType: 'geo' | 'zip'): () => void;

  searchRecruiterLocationKeyDown(): void;

  handleRecruiterLocationAreaChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleResetLinkedinSearch(): void;

  handleResetLocationSettings(): void;

  handleStartLinkedinSearch(): void;

  handleOpenRefineLinkedinSearchDialog(linkedinSearch: LinkedinSearchResult, idx: number): () => void;

  handleOpenCreateNewSearchDialog(): void;

  onOpenFeedbackDialog?(): void;

  handleSelectContactSearchObjective(newObjective: SearchObjective): () => void;

  handleOwnSearchFieldChange(fieldType: keyof SingleSearch): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handlePeopleTypeChange?: (type: 'includeReactions' | 'includeComments' | 'includeAuthor') => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleProfileDegreeChange?: (value: 1 | 2 | 3) => (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleSeparateLinkedinAccountChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAddOwnLinkedinSearch(): void;

  handleAddOwnLinkedinSearchWithSkipQualification(): void;

  handleJobTitlesChange?: (titles: string[]) => void;

  onFileDrop(files: any): void;

  handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void;

  exportCampaignContacts(): void;

  handleChangeFinalContactsPage(event: any, value: number): void;

  handleFinalContactsSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClearFinalContactsSearchField(): void;

  handleDeleteContacts(): void;

  handleAddContactsFromFile(): void;

  handleOpenRenameContactSearchDialog(search: Search): () => void;

  handleOpenDeleteContactsSearchDialog(search: Search): () => void;

  handleOpenDeleteContactsFileDialog(file: FileUpload): () => void;

  handleOpenAddSingleContactDialog(): void;

  handleAddMultipleContacts(): void;

  handleOpenDeleteContactPermanentlyDialog(contact: FunnelContact): () => void;

  handleOpenChangeAiJudgementDialog(contact: FunnelContact): () => void;

  handleOpenLoadContactsNumberDialog(search: Search): () => void;

  handleOpenEditSearchLinkedinAccountDialog(search: Search): () => void;

  handleBlacklistScopeChange(newScope: BlacklistScopes): () => void;

  handleSelectedBlacklistsChange(blacklist: Blacklist_New): () => void;

  handleOpenAddLocalBlacklistDialog(): void;

  handleOpenEditBlacklistItemsDialog(blacklist: Blacklist_New): () => void;

  handleOpenEditBlacklistNameDialog(blacklist: Blacklist_New): () => void;

  handleOpenDeleteBlacklistDialog(blacklist: Blacklist_New): () => void;

  handleRemoveSingleBlacklistItem(blacklist: Blacklist_New): (identifier: string) => () => void;

  handleSingleContactIsActiveChange(contact: FunnelContact): () => void;

  handleMultiContactIsActiveChange(): void;

  handleChangeContactsPage(event: any, value: number): void;

  handleContactsSearchChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClearContactsSearchField(): void;

  handleOpenContactDrawer(linkedInURL: string | undefined, profileId: string | undefined, campaignId: string | undefined): () => void;

  handleOpenEnrichContactsDialog(): void;

  handleSelectNodeFromList(node: Node): () => void;

  handleUnselectNodeFromList(): void;

  handleOpenFlowCreationDialog(): void;
  handleFlowBuilderApplied(campaign: Funnel): void;

  handleOpenChangeCommunicationStyleWarningDialog(): void;

  handleIncludeEmojiChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSwissSChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSecondaryLanguageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  onIsActiveChange(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Called when user changes the goal text. Accepts a textfield event. */
  onGoalChange(event: React.ChangeEvent<HTMLInputElement>): void;

  /** Called when the user updates the entire array of cases. */
  onCaseHandlingUpdate(newCaseHandling: AutoReplyCase[]): void;

  /** Called to save or finalize the auto-reply settings. (Typically no event needed.) */
  handleUpdateAutoReply(): void;

  handleMessageApprovalChange(event: React.ChangeEvent<HTMLInputElement>): void;
  handleRecontactCooldownChange(event: React.ChangeEvent<HTMLInputElement>): void;

  isLinkedinAccountMissing(): boolean;

  handleSelectedLinkedinUserChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectedTestContactChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleNextContact(): void;

  handlePrevContact(): void;

  handleNextMessage(): void;

  handlePrevMessage(): void;

  handleTestCampaign(): void;

  toggleExpanded(): void;

  handleUpdateCampaign(value: Partial<Funnel>): void;

  handleOpenStopRunningCampaignDialog(campaignBuilderStep: CampaignBuilderSteps): () => void;

  contactQualification: {
    loading: boolean;
    promptPlaceholders: QualificationPromptPlaceholders;
    selectedOptions: string[];
    rejudgeExistingContacts: boolean;
    prompt: string;
    hasOldKeys: boolean;
    isJudging: boolean;
  };

  handleAiQualifierDetailsChange(type: keyof QualificationPromptPlaceholders): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleAiQualifierPromptChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAiQualifierSelectedOptionChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleRejudgeExistingContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleIsJudgingChange(event: React.ChangeEvent<HTMLInputElement>): void;

  updateQualifyingContactsRecruiting(): void;

  updateQualifyingContactsLead(): void;

  handleRemoveContactFromBlacklist(linkedInURL: string): () => void;

  handleRemoveContactFromSuspended(linkedInURL: string): () => void;

  handleOpenUpdateSexDialog(contact: FunnelContact): () => void;

  handleRecoverContactFromDeleted(linkedInURL: string): () => void;

  handleContactFilterChange(event: React.ChangeEvent<HTMLInputElement>): void;

  applySidebarFilter(filter: ContactFilter): () => void;

  handleActiveQualificationsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleAbortProcessing(): void;

  toggleDeleteMultipleContacts(): void;

  handleMultipleContactsToDeleteChange(event: React.MouseEvent<HTMLInputElement>): void;

  handleContactsToDeleteChange(contact: FunnelContact): (event: React.MouseEvent<HTMLInputElement>) => void;

  handleOpenDeleteMultipleContactsPermanentlyDialog(): void;
}

const CampaignBuildingPanel: React.FC<Props> = ({
  productGroup,
  campaign,
  hasJudgingSettings,
  fullLinkedinAccount,
  linkedinAccountLoading,
  fullSeparateLinkedinAccount,
  autoSyncLoading,
  isUpdating,
  isFlowChanging,
  expanded,
  activeCampaignStepIndex,
  perplexityUrl,
  perplexityLoading,
  perplexityError,
  perplexityStatusMessage,
  offer,
  activeCampaignPipelineStep,
  contactSearchMethod,
  linkedinProduct,
  paiSearchLanguage,
  includeOwnContacts,
  regularLocationConstraints,
  recruiterLocationConstraints,
  linkedinSearch,
  selectedContactSearchObjective,
  searchToAdd,
  separateLinkedinAccount,
  addSearchesLoading,
  contactsToAdd,
  addContactsLoading,
  searches,
  searchesLoading,
  linkedinAccounts,
  activeBlacklistScope,
  globalBlacklists,
  localBlacklists,
  globalBlacklistsLoading,
  localBlacklistsLoading,
  removeBlacklistItemsLoading,
  contactsPage,
  contactsSearchParam,
  contactsPerPage,
  totalContacts,
  contactFilter,
  activeQualifications,
  abortRequested,
  abortLoading,
  deleteMultipleContacts,
  contactsToDelete,
  aiJudgementProcessing,
  profileAnalysisProcessing,
  contacts,
  contactsLoading,
  campaignTesting,
  finalContacts,
  finalContactsPage,
  finalContactsSearchParam,
  finalContactsPerPage,
  totalFinalContacts,
  finalContactsLoading,
  finalContactsProcessing,
  exportFinalContactsLoading,
  selectCampaignStep,
  handlePerplexityUrlChange,
  handleOfferInputChange,
  handleOfferToggleChange,
  handlePerplexityPaste,
  handlePerplexityKeyDown,
  handlePerplexityRefresh,
  handleActiveCampaignPipelineStepChange,
  handleContactSearchMethodChange,
  handleMoveToNewSearch,
  handleLinkedinProductChange,
  handlePaiSearchLanguageChange,
  handleIncludeOwnContactsChange,
  handleLocationFieldChange,
  searchKeyUp,
  searchKeyDown,
  handleMultiSelectLocationsChange,
  handleMultiSelectLocationsRemove,
  handleSelectRecruiterLocationConstraint,
  handleRemoveRecruiterLocationConstraint,
  handleRecruiterLocationConstraintQueryChange,
  searchRecruiterLocationKeyUp,
  searchRecruiterLocationKeyDown,
  handleRecruiterLocationAreaChange,
  handleResetLinkedinSearch,
  handleResetLocationSettings,
  handleStartLinkedinSearch,
  handleOpenRefineLinkedinSearchDialog,
  handleOpenCreateNewSearchDialog,
  onOpenFeedbackDialog,
  handleSelectContactSearchObjective,
  handleOwnSearchFieldChange,
  handlePeopleTypeChange,
  handleProfileDegreeChange,
  handleSeparateLinkedinAccountChange,
  handleAddOwnLinkedinSearch,
  handleAddOwnLinkedinSearchWithSkipQualification,
  handleJobTitlesChange,
  onFileDrop,
  handleFileChange,
  exportCampaignContacts,
  handleChangeFinalContactsPage,
  handleFinalContactsSearchChange,
  handleClearFinalContactsSearchField,
  handleDeleteContacts,
  handleAddContactsFromFile,
  handleOpenRenameContactSearchDialog,
  handleOpenDeleteContactsSearchDialog,
  handleOpenDeleteContactsFileDialog,
  handleOpenAddSingleContactDialog,
  handleAddMultipleContacts,
  handleOpenDeleteContactPermanentlyDialog,
  handleOpenChangeAiJudgementDialog,
  handleOpenLoadContactsNumberDialog,
  handleOpenEditSearchLinkedinAccountDialog,
  handleBlacklistScopeChange,
  handleSelectedBlacklistsChange,
  handleOpenAddLocalBlacklistDialog,
  handleOpenEditBlacklistItemsDialog,
  handleOpenEditBlacklistNameDialog,
  handleOpenDeleteBlacklistDialog,
  handleRemoveSingleBlacklistItem,
  handleSingleContactIsActiveChange,
  handleMultiContactIsActiveChange,
  handleChangeContactsPage,
  handleContactsSearchChange,
  handleClearContactsSearchField,
  handleOpenContactDrawer,
  handleOpenEnrichContactsDialog,
  handleSelectNodeFromList,
  handleUnselectNodeFromList,
  handleOpenFlowCreationDialog,
  handleFlowBuilderApplied,
  handleOpenChangeCommunicationStyleWarningDialog,
  handleIncludeEmojiChange,
  handleSwissSChange,
  handleLanguageChange,
  handleSecondaryLanguageChange,
  onIsActiveChange,
  onGoalChange,
  onCaseHandlingUpdate,
  handleUpdateAutoReply,
  handleMessageApprovalChange,
  handleRecontactCooldownChange,
  isLinkedinAccountMissing,
  handleSelectedLinkedinUserChange,
  handleSelectedTestContactChange,
  handleNextContact,
  handlePrevContact,
  handleNextMessage,
  handlePrevMessage,
  handleTestCampaign,
  toggleExpanded,
  handleUpdateCampaign,
  handleOpenStopRunningCampaignDialog,
  contactQualification,
  handleAiQualifierDetailsChange,
  handleAiQualifierPromptChange,
  handleAiQualifierSelectedOptionChange,
  handleRejudgeExistingContactsChange,
  handleIsJudgingChange,
  updateQualifyingContactsRecruiting,
  updateQualifyingContactsLead,
  handleRemoveContactFromBlacklist,
  handleRemoveContactFromSuspended,
  handleOpenUpdateSexDialog,
  handleRecoverContactFromDeleted,
  handleContactFilterChange,
  applySidebarFilter,
  handleActiveQualificationsChange,
  handleAbortProcessing,
  toggleDeleteMultipleContacts,
  handleMultipleContactsToDeleteChange,
  handleContactsToDeleteChange,
  handleOpenDeleteMultipleContactsPermanentlyDialog,
}) => {
  const { classes } = useStyles({ expanded });
  const { t } = useTranslation();

  const selectedNode = useCampaignNodeStore((state) => state.selectedNode);

  const {
    _id = '',
    type = FunnelTypes.ACTIVE_SOURCING_CO_PILOT,
    status = FunnelStatuses.CREATED,
    linkedinAccount = '',
    settings,
    autoReply = {
      isActive: false,
      goalofCommunication: '',
      caseHandling: [],
    },
  } = campaign || {};
  const blacklists: string[] = ((campaign as any)?.blacklistIds ?? (campaign as any)?.blacklists ?? []) as string[];
  const approveMessages = settings?.approveMessages ?? false;
  // Top-level on the campaign, not under `settings` — matches the API field.
  const recontactCooldownDays = (campaign as any)?.recontactCooldownDays as number | undefined;
  const politeSpeech = settings?.formalSpeech ?? false;
  const includeEmoji = settings?.includeEmoji ?? false;
  const swissS = settings?.swissS ?? false;
  const language = settings?.language ?? 'auto';
  const secondaryLanguage = settings?.secondaryLanguage ?? 'none';
  const campaignStepsArray = _.map(CampaignBuilderSteps, (tab) => tab);
  const activeCampaignStepString = campaignStepsArray[activeCampaignStepIndex];

  const separateLinkedinAccountFullName = linkedinAccounts.find((account) => account._id === separateLinkedinAccount)?.userEmail || '';

  const isCampaignRunning =
    status === FunnelStatuses.STARTED || status === FunnelStatuses.PAUSED || status === FunnelStatuses.CONTACTS_ALMOST_EXHAUSTED;

  const mapToArray = (map: Record<string, string> | undefined): CustomPlaceholder[] => {
    if (!map) return [];
    return Object.entries(map).map(([k, v]) => ({ key: k, value: v }));
  };

  const arrayToMap = (arr: CustomPlaceholder[]): Record<string, string> => {
    return arr.reduce((acc, cur) => {
      acc[cur.key] = cur.value;
      return acc;
    }, {} as Record<string, string>);
  };

  const [customPlaceholders, setCustomPlaceholders] = useState<CustomPlaceholder[]>(
    mapToArray((campaign as any)?.offer?.customPlaceholders as Record<string, string>)
  );

  useEffect(() => {
    const campaignPlaceholders = (campaign as any)?.offer?.customPlaceholders;

    if (campaignPlaceholders !== undefined) {
      setCustomPlaceholders(mapToArray(campaignPlaceholders as Record<string, string>));
    }
  }, [campaign]);

  const handleAddCustomPlaceholder = (key: string, value: string) => {
    if (!key.trim() || !value.trim()) return;
    setCustomPlaceholders((prev) => {
      const updated = prev.filter((p) => p.key !== key.trim());
      const newList = [...updated, { key: key.trim(), value: value.trim() }];
      handleUpdateCampaign({ offer: { ...(offer || {}), customPlaceholders: arrayToMap(newList) } } as any);
      return newList;
    });
  };

  const handleDeleteCustomPlaceholder = (key: string) => {
    setCustomPlaceholders((prev) => {
      const newList = prev.filter((p) => p.key !== key);
      handleUpdateCampaign({ offer: { ...(offer || {}), customPlaceholders: arrayToMap(newList) } } as any);
      return newList;
    });
  };

  const handleEditCustomPlaceholder = (originalKey: string, newKey: string, newValue: string) => {
    if (!newKey.trim()) return;
    setCustomPlaceholders((prev) => {
      const filtered = prev.filter((p) => p.key !== originalKey);
      const updated = [...filtered, { key: newKey.trim(), value: newValue.trim() }];
      handleUpdateCampaign({ offer: { ...(offer || {}), customPlaceholders: arrayToMap(updated) } } as any);
      return updated;
    });
  };

  const getCampaignStepContent = () => {
    switch (activeCampaignStepString) {
      case CampaignBuilderSteps.CAMPAIGN_BRIEFING:
        return (
          <BriefAi
            productGroup={productGroup}
            perplexityUrl={perplexityUrl}
            perplexityLoading={perplexityLoading}
            perplexityError={perplexityError}
            perplexityStatusMessage={perplexityStatusMessage}
            offer={offer}
            customPlaceholders={customPlaceholders}
            handleAddCustomPlaceholder={handleAddCustomPlaceholder}
            handleDeleteCustomPlaceholder={handleDeleteCustomPlaceholder}
            handleEditCustomPlaceholder={handleEditCustomPlaceholder}
            handlePerplexityUrlChange={handlePerplexityUrlChange}
            handleOfferInputChange={handleOfferInputChange}
            handleOfferToggleChange={handleOfferToggleChange}
            handlePerplexityPaste={handlePerplexityPaste}
            handlePerplexityKeyDown={handlePerplexityKeyDown}
            handlePerplexityRefresh={handlePerplexityRefresh}
          />
        );
      case CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST:
        return (
          <CampaignPipeline
            productGroup={productGroup}
            hasJudgingSettings={hasJudgingSettings}
            blacklistSyncLoading={isUpdating}
            activeCampaignPipelineStep={activeCampaignPipelineStep}
            contactSearchMethod={contactSearchMethod}
            funnelId={_id ?? ''}
            funnelType={type}
            linkedinProduct={linkedinProduct}
            paiSearchLanguage={paiSearchLanguage}
            includeOwnContacts={includeOwnContacts}
            regularLocationConstraints={regularLocationConstraints}
            recruiterLocationConstraints={recruiterLocationConstraints}
            linkedinSearch={linkedinSearch}
            selectedContactSearchObjective={selectedContactSearchObjective}
            searchToAdd={searchToAdd}
            linkedinAccounts={linkedinAccounts}
            linkedinAccount={fullSeparateLinkedinAccount || fullLinkedinAccount}
            separateLinkedinAccount={separateLinkedinAccount}
            addSearchesLoading={addSearchesLoading}
            contactsToAdd={contactsToAdd}
            addContactsLoading={addContactsLoading}
            searches={searches}
            searchesLoading={searchesLoading}
            activeBlacklistScope={activeBlacklistScope}
            globalBlacklists={globalBlacklists}
            localBlacklists={localBlacklists}
            globalBlacklistsLoading={globalBlacklistsLoading}
            localBlacklistsLoading={localBlacklistsLoading}
            removeBlacklistItemsLoading={removeBlacklistItemsLoading}
            blacklists={blacklists}
            contactsPage={contactsPage}
            contactsSearchParam={contactsSearchParam}
            contactsPerPage={contactsPerPage}
            totalContacts={totalContacts}
            contactFilter={contactFilter}
            activeQualifications={activeQualifications}
            abortRequested={abortRequested}
            abortLoading={abortLoading}
            deleteMultipleContacts={deleteMultipleContacts}
            contactsToDelete={contactsToDelete}
            aiJudgementProcessing={aiJudgementProcessing}
            profileAnalysisProcessing={profileAnalysisProcessing}
            contacts={contacts}
            contactsLoading={contactsLoading}
            finalContacts={finalContacts}
            finalContactsPage={finalContactsPage}
            finalContactsSearchParam={finalContactsSearchParam}
            finalContactsPerPage={finalContactsPerPage}
            totalFinalContacts={totalFinalContacts}
            finalContactsLoading={finalContactsLoading}
            exportFinalContactsLoading={exportFinalContactsLoading}
            contactQualification={contactQualification}
            handleAiQualifierDetailsChange={handleAiQualifierDetailsChange}
            handleAiQualifierPromptChange={handleAiQualifierPromptChange}
            handleAiQualifierSelectedOptionChange={handleAiQualifierSelectedOptionChange}
            handleRejudgeExistingContactsChange={handleRejudgeExistingContactsChange}
            handleIsJudgingChange={handleIsJudgingChange}
            updateQualifyingContactsRecruiting={updateQualifyingContactsRecruiting}
            updateQualifyingContactsLead={updateQualifyingContactsLead}
            handleContactSearchMethodChange={handleContactSearchMethodChange}
            handleMoveToNewSearch={handleMoveToNewSearch}
            handleLinkedinProductChange={handleLinkedinProductChange}
            handlePaiSearchLanguageChange={handlePaiSearchLanguageChange}
            handleIncludeOwnContactsChange={handleIncludeOwnContactsChange}
            handleLocationFieldChange={handleLocationFieldChange}
            searchKeyUp={searchKeyUp}
            searchKeyDown={searchKeyDown}
            handleMultiSelectLocationsChange={handleMultiSelectLocationsChange}
            handleMultiSelectLocationsRemove={handleMultiSelectLocationsRemove}
            handleSelectRecruiterLocationConstraint={handleSelectRecruiterLocationConstraint}
            handleRemoveRecruiterLocationConstraint={handleRemoveRecruiterLocationConstraint}
            handleRecruiterLocationConstraintQueryChange={handleRecruiterLocationConstraintQueryChange}
            searchRecruiterLocationKeyUp={searchRecruiterLocationKeyUp}
            searchRecruiterLocationKeyDown={searchRecruiterLocationKeyDown}
            handleRecruiterLocationAreaChange={handleRecruiterLocationAreaChange}
            handleResetLinkedinSearch={handleResetLinkedinSearch}
            handleResetLocationSettings={handleResetLocationSettings}
            handleStartLinkedinSearch={handleStartLinkedinSearch}
            handleOpenRefineLinkedinSearchDialog={handleOpenRefineLinkedinSearchDialog}
            handleOpenCreateNewSearchDialog={handleOpenCreateNewSearchDialog}
            onOpenFeedbackDialog={onOpenFeedbackDialog}
            handleSelectContactSearchObjective={handleSelectContactSearchObjective}
            handleOwnSearchFieldChange={handleOwnSearchFieldChange}
            handlePeopleTypeChange={handlePeopleTypeChange}
            handleProfileDegreeChange={handleProfileDegreeChange}
            handleSeparateLinkedinAccountChange={handleSeparateLinkedinAccountChange}
            handleAddOwnLinkedinSearch={handleAddOwnLinkedinSearch}
            handleAddOwnLinkedinSearchWithSkipQualification={handleAddOwnLinkedinSearchWithSkipQualification}
            handleJobTitlesChange={handleJobTitlesChange}
            onFileDrop={onFileDrop}
            handleFileChange={handleFileChange}
            exportCampaignContacts={exportCampaignContacts}
            handleChangeFinalContactsPage={handleChangeFinalContactsPage}
            handleFinalContactsSearchChange={handleFinalContactsSearchChange}
            handleClearFinalContactsSearchField={handleClearFinalContactsSearchField}
            handleDeleteContacts={handleDeleteContacts}
            handleAddContactsFromFile={handleAddContactsFromFile}
            handleOpenRenameContactSearchDialog={handleOpenRenameContactSearchDialog}
            handleOpenDeleteContactsSearchDialog={handleOpenDeleteContactsSearchDialog}
            handleOpenDeleteContactsFileDialog={handleOpenDeleteContactsFileDialog}
            handleOpenAddSingleContactDialog={handleOpenAddSingleContactDialog}
            handleAddMultipleContacts={handleAddMultipleContacts}
            handleOpenDeleteContactPermanentlyDialog={handleOpenDeleteContactPermanentlyDialog}
            handleOpenChangeAiJudgementDialog={handleOpenChangeAiJudgementDialog}
            handleOpenLoadContactsNumberDialog={handleOpenLoadContactsNumberDialog}
            handleOpenEditSearchLinkedinAccountDialog={handleOpenEditSearchLinkedinAccountDialog}
            handleBlacklistScopeChange={handleBlacklistScopeChange}
            handleSelectedBlacklistsChange={handleSelectedBlacklistsChange}
            handleOpenAddLocalBlacklistDialog={handleOpenAddLocalBlacklistDialog}
            handleOpenEditBlacklistItemsDialog={handleOpenEditBlacklistItemsDialog}
            handleOpenEditBlacklistNameDialog={handleOpenEditBlacklistNameDialog}
            handleOpenDeleteBlacklistDialog={handleOpenDeleteBlacklistDialog}
            handleRemoveSingleBlacklistItem={handleRemoveSingleBlacklistItem}
            handleSingleContactIsActiveChange={handleSingleContactIsActiveChange}
            handleMultiContactIsActiveChange={handleMultiContactIsActiveChange}
            handleChangeContactsPage={handleChangeContactsPage}
            handleContactsSearchChange={handleContactsSearchChange}
            handleClearContactsSearchField={handleClearContactsSearchField}
            handleOpenContactDrawer={handleOpenContactDrawer}
            handleOpenEnrichContactsDialog={handleOpenEnrichContactsDialog}
            handleRemoveContactFromBlacklist={handleRemoveContactFromBlacklist}
            handleRemoveContactFromSuspended={handleRemoveContactFromSuspended}
            handleOpenUpdateSexDialog={handleOpenUpdateSexDialog}
            handleRecoverContactFromDeleted={handleRecoverContactFromDeleted}
            handleContactFilterChange={handleContactFilterChange}
            handleActiveQualificationsChange={handleActiveQualificationsChange}
            handleAbortProcessing={handleAbortProcessing}
            toggleDeleteMultipleContacts={toggleDeleteMultipleContacts}
            handleMultipleContactsToDeleteChange={handleMultipleContactsToDeleteChange}
            handleContactsToDeleteChange={handleContactsToDeleteChange}
            handleOpenDeleteMultipleContactsPermanentlyDialog={handleOpenDeleteMultipleContactsPermanentlyDialog}
          />
        );
      case CampaignBuilderSteps.CAMPAIGN_FLOW:
        return (
          <CampaignFlowManager
            campaignId={_id}
            isFlowChanging={isFlowChanging}
            isUpdating={isUpdating}
            linkedinAccount={fullLinkedinAccount}
            isFormal={politeSpeech}
            includeEmoji={includeEmoji}
            swissS={swissS}
            language={language}
            secondaryLanguage={secondaryLanguage as string}
            productGroup={productGroup}
            offer={offer}
            customPlaceholders={customPlaceholders}
            handleSelectNodeFromList={handleSelectNodeFromList}
            handleUnselectNodeFromList={handleUnselectNodeFromList}
            handleOpenFlowCreationDialog={handleOpenFlowCreationDialog}
            handleFlowBuilderApplied={handleFlowBuilderApplied}
            handleOpenChangeCommunicationStyleWarningDialog={handleOpenChangeCommunicationStyleWarningDialog}
            handleIncludeEmojiChange={handleIncludeEmojiChange}
            handleSwissSChange={handleSwissSChange}
            handleLanguageChange={handleLanguageChange}
            handleSecondaryLanguageChange={handleSecondaryLanguageChange}
            autoReply={autoReply}
            onIsActiveChange={onIsActiveChange}
            onGoalChange={onGoalChange}
            onCaseHandlingUpdate={onCaseHandlingUpdate}
            handleUpdateAutoReply={handleUpdateAutoReply}
            handleOfferInputChange={handleOfferInputChange}
            handleOfferToggleChange={handleOfferToggleChange}
            handleAddCustomPlaceholder={handleAddCustomPlaceholder}
            handleDeleteCustomPlaceholder={handleDeleteCustomPlaceholder}
            handleEditCustomPlaceholder={handleEditCustomPlaceholder}
          />
        );
      case CampaignBuilderSteps.CAMPAIGN_SETTINGS:
        return (
          <CampaignSettings
            status={status}
            approveMessages={approveMessages}
            recontactCooldownDays={recontactCooldownDays}
            linkedinAccounts={linkedinAccounts}
            selectedLinkedinAccount={linkedinAccount}
            handleMessageApprovalChange={handleMessageApprovalChange}
            handleRecontactCooldownChange={handleRecontactCooldownChange}
            isLinkedinAccountMissing={isLinkedinAccountMissing}
            handleSelectedLinkedinUserChange={handleSelectedLinkedinUserChange}
          />
        );
      case CampaignBuilderSteps.CAMPAIGN_TESTING:
        return (
          <CampaignTesting
            campaignTesting={campaignTesting}
            finalContacts={finalContacts}
            finalContactsProcessing={finalContactsProcessing}
            autoSyncLoading={autoSyncLoading}
            productGroup={productGroup}
            handleSelectedTestContactChange={handleSelectedTestContactChange}
            handleNextContact={handleNextContact}
            handlePrevContact={handlePrevContact}
            handleNextMessage={handleNextMessage}
            handlePrevMessage={handlePrevMessage}
            handleTestCampaign={handleTestCampaign}
          />
        );
      case CampaignBuilderSteps.CAMPAIGN_SUMMARY:
        return (
          <CampaignSummary
            campaign={campaign}
            productGroup={productGroup}
            finalContacts={finalContacts}
            totalFinalContacts={totalFinalContacts}
            fullLinkedinAccount={fullLinkedinAccount}
            linkedinAccountLoading={linkedinAccountLoading}
            selectCampaignStep={selectCampaignStep}
            isCampaignRunning={isCampaignRunning}
            handleOpenStopRunningCampaignDialog={handleOpenStopRunningCampaignDialog}
          />
        );
      default:
        return <></>;
    }
  };

  const getNodeTypeLabel = (node: Node) => {
    switch (node.type) {
      case NodeVariants.FUNCTION_NODE:
        return t('message');
      case NodeVariants.WAIT_NODE:
        return t('waitingTime');
      case NodeVariants.CONDITIONAL_NODE:
      case NodeVariants.CONDITION_WITH_DURATION_NODE:
        return t('condition');
      case NodeVariants.UTILITY_NODE:
        return t('utility');
      default:
        return t('noActionSelected');
    }
  };

  const isFlowStep = activeCampaignStepString === CampaignBuilderSteps.CAMPAIGN_FLOW;
  const showNodeInspector = isFlowStep && !_.isNil(selectedNode);

  const getPanelCaption = () => (showNodeInspector ? t('selectedStep') : t('currentStep'));

  const getPanelTitle = () => {
    if (!showNodeInspector || _.isNil(selectedNode)) return t(activeCampaignStepString);

    const functionNumber = selectedNode.data?.functionNumber;
    const functionName = selectedNode.data?.functionName;
    const label = functionName ? t(functionName) : getNodeTypeLabel(selectedNode);

    return _.isNil(functionNumber) ? label : `${functionNumber} · ${label}`;
  };

  return (
    <Stack className={classes.campaignBuildingPanel} direction={'row'}>
      {isFlowStep ? (
        <IconButton className={classes.expandButton} onClick={toggleExpanded}>
          <ChevronLeftIcon />
        </IconButton>
      ) : null}
      {expanded ? (
        <StepsSidebar
          productGroup={productGroup}
          activeCampaignStep={activeCampaignStepString}
          activeCampaignPipelineStep={activeCampaignPipelineStep}
          contactSearchMethod={contactSearchMethod}
          linkedinProduct={linkedinProduct}
          paiSearchLanguage={paiSearchLanguage}
          regularLocationConstraints={regularLocationConstraints}
          recruiterLocationConstraints={recruiterLocationConstraints}
          selectedContactSearchObjective={selectedContactSearchObjective}
          searchToAdd={searchToAdd}
          separateLinkedinAccount={separateLinkedinAccountFullName}
          contactsToAdd={contactsToAdd}
          contactFilter={contactFilter}
          handleActiveCampaignPipelineStepChange={handleActiveCampaignPipelineStepChange}
          applySidebarFilter={applySidebarFilter}
        />
      ) : null}
      <Stack className={classes.panelContent}>
        <Stack className={classes.panelHead} gap={0.5}>
          <Typography className={classes.panelCaption}>{getPanelCaption()}</Typography>
          <Typography className={classes.panelTitle}>{getPanelTitle()}</Typography>
        </Stack>
        <div id={SelectorIds.STEP_CONTENT} className={classes.stepContent}>
          {getCampaignStepContent()}
        </div>
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles<{ expanded: boolean }>()((theme, { expanded }) => ({
  campaignBuildingPanel: {
    position: 'relative',
    flexBasis: !expanded ? '480px' : '100%',
    maxWidth: !expanded ? '480px' : '100%',
    flexShrink: 0,
    height: '100%',
    width: '100%',
    borderLeft: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: '#FFF',
    overflow: 'hidden',
    fontSize: theme.typography.pxToRem(14),
  },
  expandButton: {
    position: 'absolute',
    background: '#FFF',
    zIndex: 99,
    left: expanded ? '16px' : '-16px',
    top: '50%',
    border: `1px solid ${theme.palette.greys.light}`,
    padding: '4px',
    '& .MuiSvgIcon-root ': {
      fontSize: theme.typography.pxToRem(24),
      transition: '225ms',
      transform: expanded ? 'rotate(180deg)' : undefined,
    },
    '&:hover': {
      background: darken('#FFF', 0.05),
    },
  },
  panelContent: {
    flexBasis: expanded ? 'calc(100% - 350px)' : '100%',
    maxWidth: expanded ? 'calc(100% - 350px)' : '100%',
    minWidth: 0,
    height: '100%',
    position: 'relative',
  },
  panelHead: {
    flexShrink: 0,
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  panelCaption: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  panelTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 500,
  },
  stepContent: {
    flex: 1,
    minHeight: 0,
    padding: '20px 24px',
    background: '#FFF',
    overflow: 'auto',
  },
}));

export default CampaignBuildingPanel;
